import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Wand2, BarChart2, Clock, Zap, TrendingUp, History } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import WaveformBars from '../WaveformBars';

const StatCard = ({ icon, label, value, sub, color }) => (
  <div className="glass-card card-hover rounded-2xl p-5 border border-border">
    <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${color}`}>
      {icon}
    </div>
    <p className="text-2xl sm:text-3xl font-bold font-outfit text-foreground">{value}</p>
    <p className="text-sm font-medium text-foreground mt-1">{label}</p>
    {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
  </div>
);

const Overview = () => {
  const { user, token, API } = useAuth();
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const headers = { Authorization: `Bearer ${token}` };
    Promise.all([
      axios.get(`${API}/stats/dashboard`, { headers }),
      axios.get(`${API}/tts/history?limit=5`, { headers })
    ]).then(([s, h]) => {
      setStats(s.data);
      setRecent(h.data.history || []);
    }).catch(console.error).finally(() => setLoading(false));
  }, [token, API]);

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-10 h-10 border-2 border-primary border-t-transparent rounded-full animate-spin" />
    </div>
  );

  const cards = [
    {
      icon: <Wand2 className="w-5 h-5 text-blue-500" />,
      label: "Total Generations",
      value: (stats?.total_generations || 0).toLocaleString(),
      sub: `${stats?.recent_generations || 0} this week`,
      color: "bg-blue-500/10"
    },
    {
      icon: <BarChart2 className="w-5 h-5 text-purple-500" />,
      label: "Characters Generated",
      value: (stats?.total_chars_generated || 0).toLocaleString(),
      sub: "Lifetime total",
      color: "bg-purple-500/10"
    },
    {
      icon: <Zap className="w-5 h-5 text-green-500" />,
      label: "Credits Remaining",
      value: (stats?.credits_remaining || 0).toLocaleString(),
      sub: `${stats?.credits_used || 0} used`,
      color: "bg-green-500/10"
    },
    {
      icon: <TrendingUp className="w-5 h-5 text-cyan-500" />,
      label: "This Week",
      value: (stats?.recent_generations || 0).toLocaleString(),
      sub: "Voice generations",
      color: "bg-cyan-500/10"
    },
  ];

  const weeklyData = stats?.weekly_data || [];

  return (
    <div data-testid="overview-page" className="space-y-6 overflow-x-hidden">
      {/* Welcome */}
      <div className="glass-card rounded-2xl p-6 sm:p-8 border border-border relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 to-purple-500/5 -z-0" />
        <div className="relative z-10 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="min-w-0">
            <h2 className="text-xl sm:text-2xl font-bold font-outfit text-foreground truncate">
              Welcome back, <span className="text-gradient">{user?.name?.split(' ')[0]}</span>!
            </h2>
            <p className="text-sm text-muted-foreground mt-1">Create amazing voice content with AI</p>
          </div>
          <div className="hidden sm:flex items-center gap-4 flex-shrink-0">
            <WaveformBars isPlaying={true} count={20} height={36} />
          </div>
        </div>
      </div>

      {/* Stats grid - responsive */}
      <div data-testid="stats-grid" className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-5">
        {cards.map((c, i) => <StatCard key={i} {...c} />)}
      </div>

      {/* Weekly Activity */}
      {weeklyData.length > 0 && (
        <div className="glass-card rounded-2xl p-5 sm:p-6 border border-border">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="w-5 h-5 text-primary" />
            <h3 className="font-semibold font-outfit text-foreground">This Week's Activity</h3>
          </div>
          <div className="flex items-end gap-2 sm:gap-4 h-32">
            {weeklyData.map((d, i) => {
              const maxCount = Math.max(...weeklyData.map(w => w.count), 1);
              const height = Math.max(8, (d.count / maxCount) * 100);
              return (
                <div key={i} className="flex-1 flex flex-col items-center gap-1">
                  <span className="text-xs text-muted-foreground">{d.count}</span>
                  <div
                    className="w-full rounded-t-lg bg-gradient-to-t from-blue-500 to-purple-500 transition-all"
                    style={{ height: `${height}%`, minHeight: '4px' }}
                  />
                  <span className="text-xs text-muted-foreground">{d.day}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Credits bar */}
      <div className="glass-card rounded-2xl p-5 sm:p-6 border border-border">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold font-outfit text-foreground">Credits Usage</h3>
          <span className="text-xs sm:text-sm text-muted-foreground capitalize">{user?.plan || 'free'} plan</span>
        </div>
        <div className="h-3 bg-secondary rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-blue-500 to-purple-500 rounded-full transition-all"
            style={{ width: `${Math.min(100, ((stats?.credits_used || 0) / (user?.credits || 50000)) * 100)}%` }}
          />
        </div>
        <div className="flex justify-between text-xs text-muted-foreground mt-2">
          <span>{(stats?.credits_used || 0).toLocaleString()} used</span>
          <span>{(user?.credits || 50000).toLocaleString()} total</span>
        </div>
      </div>

      {/* Recent history - Responsive */}
      {recent.length > 0 && (
        <div className="glass-card rounded-2xl p-5 sm:p-6 border border-border">
          <div className="flex items-center gap-2 mb-4">
            <History className="w-5 h-5 text-primary" />
            <h3 className="font-semibold font-outfit text-foreground">Recent Generations</h3>
          </div>
          <div className="space-y-2">
            {recent.map((item, i) => (
              <div key={i} data-testid={`recent-item-${i}`}
                className="flex items-center gap-3 p-3 rounded-xl bg-secondary/50 hover:bg-secondary transition-colors overflow-hidden">
                <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                  {item.voice_name?.[0] || item.voice?.[0] || 'V'}
                </div>
                <div className="flex-1 min-w-0 overflow-hidden">
                  <p className="text-sm font-medium text-foreground truncate">{item.text || 'Generated audio'}</p>
                  <p className="text-xs text-muted-foreground truncate">
                    {item.voice_name || item.voice} · {item.text_length} chars
                    {item.style && item.style !== 'default' ? ` · ${item.style}` : ''}
                  </p>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <Clock className="w-3 h-3 text-muted-foreground hidden sm:block" />
                  <span className="text-xs text-muted-foreground whitespace-nowrap">
                    {new Date(item.created_at).toLocaleDateString()}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Overview;
